import { useMutation } from "@apollo/client";
import { compact, sortBy } from "lodash";
import { useCallback } from "react";
import {
  AssessmentDeliveryState,
  AssessmentGroupDelivery,
  AssessmentSubmissionNodeType,
  ComplianceProgramCalibrationStatus,
  ComplianceProgramParticipantStatus,
  RelatedAssessmentSubmissionFragment,
  SaveAssessmentDeliveryMutation,
  SaveAssessmentDeliveryMutationVariables,
  UnsubmitAssessmentMutation,
  UnsubmitAssessmentMutationVariables,
  UpdateCalibrationStatusMutation,
  UpdateCalibrationStatusMutationVariables,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import { numberAnonymousReviews } from "@apps/assessments/components/reopen-review-numbers";
import ReopenReviewOptionLabel from "@apps/assessments/components/reopen-review-option-label";
import getAssessmentDelivery from "@apps/assessments/graphql/get-assessment-delivery";
import getReviewSubmissionsQuery from "@apps/assessments/graphql/get-review-submissions-query";
import reopenAssessmentMutation from "@apps/assessments/graphql/reopen-assessment-mutation";
import updateAssessmentDeliveryMutation from "@apps/assessments/graphql/update-assessment-delivery-mutation";
import {
  getCalibrateConfirmation,
  getDeliveryStateChangeConfirmation,
  getDeliveryStateChangeSuccess,
  getReopenConfirmation,
} from "@apps/reporting/components/assessment-report/confirmation-strings";
import updateCalibrationStatusMutation from "@apps/reporting/graphql/update-calibration-status-mutation";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import { assertEdgesNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

const SingleSubmissionActions = ({
  submission,
  isDisabled,
  onReopenAssessment,
}: {
  submission: RelatedAssessmentSubmissionFragment;
  isDisabled: boolean;
  onReopenAssessment: (assessmentId: number) => void;
}) => {
  const label = useLabel();
  const { ConfirmationDialog, confirm } = useConfirm(
    "Are you sure?",
    getReopenConfirmation(label),
  );

  const handleReopen = useCallback(
    async (assessmentId: number) => {
      const confirmation = await confirm();
      if (confirmation) {
        onReopenAssessment(assessmentId);
      }
    },
    [confirm, onReopenAssessment],
  );

  const editableAssessments = submission.template
    ? assertEdgesNonNull(submission.template?.assessments)
        .filter(({ canUpdate }) => canUpdate?.permission)
        .filter(
          (assessment) =>
            (submission.type === AssessmentSubmissionNodeType.SelfAssessment &&
              assessment.responder?.id === assessment.target?.id) ||
            (submission.type ===
              AssessmentSubmissionNodeType.ManagerAssessment &&
              assessment.responder?.id !== assessment.target?.id),
        )
    : [];

  if (editableAssessments.length === 1) {
    return (
      <>
        <ConfirmationDialog />
        <Button
          disabled={isDisabled}
          size={ButtonSize.medium}
          onClick={() => handleReopen(editableAssessments[0].id)}
        >
          Reopen
        </Button>
      </>
    );
  }

  return <div></div>;
};

const MultiSubmissionActions = ({
  submission,
  isDisabled,
  onReopenAssessments,
}: {
  submission: RelatedAssessmentSubmissionFragment;
  isDisabled: boolean;
  onReopenAssessments: (assessmentIds: number[]) => void;
}) => {
  const label = useLabel();

  const { ConfirmationDialog, confirm, setMessage } = useConfirm(
    "Are you sure?",
    "Are you sure?",
  );

  const handleReopen = useCallback(
    async (assessmentIds: number[]) => {
      setMessage(getReopenConfirmation(label, assessmentIds.length));
      const confirmation = await confirm();
      if (confirmation) {
        onReopenAssessments(assessmentIds);
      }
    },
    [confirm, label, onReopenAssessments, setMessage],
  );

  // In id order, so the menu lists the anonymous reviews as Peer Review 1, 2, 3.
  const submittedAssessments = submission.template
    ? sortBy(assertEdgesNonNull(submission.template.assessments), "id")
    : [];
  // Numbered before the filter, so this menu and the Results menu give a review the same number.
  const anonymousNumbers = numberAnonymousReviews(submittedAssessments);
  const editableAssessments = submittedAssessments.filter(
    ({ canUpdate }) => canUpdate?.permission,
  );

  const assessmentType =
    submission.type === AssessmentSubmissionNodeType.UpwardAssessment
      ? "Upward"
      : "Peer";

  const options: DropdownOptionType[] = compact([
    editableAssessments.length > 1 && {
      label: (
        <div className="font-medium">{`Reopen All Submitted ${assessmentType} ${label("review", { capitalize: true, pluralize: true })} (${editableAssessments.length})`}</div>
      ),
      onClick: () => handleReopen(editableAssessments.map(({ id }) => id)),
    },
    editableAssessments.length > 1 && { type: "divider" },
    ...editableAssessments.map((assessment) => ({
      label: (
        <ReopenReviewOptionLabel
          assessmentType={assessmentType}
          review={assessment}
          submittedDatetime={assessment.submittedDatetime}
          number={anonymousNumbers.get(assessment.id)}
        />
      ),
      onClick: () => handleReopen([assessment.id]),
    })),
  ]);

  if (editableAssessments.length >= 1) {
    return (
      <>
        <ConfirmationDialog />
        <Dropdown disabled={isDisabled} options={options}>
          <Button size={ButtonSize.medium} showDownChevronIcon>
            Reopen
          </Button>
        </Dropdown>
      </>
    );
  }

  return <div></div>;
};

const CalibrationActions = ({
  submission,
  complianceProgramId,
  target,
}: {
  submission: RelatedAssessmentSubmissionFragment;
  complianceProgramId: number;
  target: BasicUser;
}) => {
  const label = useLabel();

  const { ConfirmationDialog, confirm, setMessage } = useConfirm(
    "Are you sure?",
    "Are you sure?",
  );

  const [updateCalibrationStatus, { loading }] = useMutation<
    UpdateCalibrationStatusMutation,
    UpdateCalibrationStatusMutationVariables
  >(updateCalibrationStatusMutation);

  const handleUpdateCalibrationStatus = useCallback(
    async (status: ComplianceProgramCalibrationStatus, statusStr: string) => {
      setMessage(
        getCalibrateConfirmation({
          label,
        }),
      );
      const confirmation =
        status === ComplianceProgramCalibrationStatus.Calibrated
          ? await confirm()
          : true;
      if (confirmation) {
        updateCalibrationStatus({
          variables: {
            complianceProgramId,
            targetId: target.id,
            status,
          },
          refetchQueries: [getReviewSubmissionsQuery],
          onCompleted: () => {
            successNotificationVar({
              title: `${label("review", {
                capitalize: true,
              })} Package marked as ${statusStr}`,
            });
          },
        });
      }
    },
    [
      complianceProgramId,
      confirm,
      label,
      setMessage,
      target,
      updateCalibrationStatus,
    ],
  );

  const options: DropdownOptionType[] = compact([
    submission.status === ComplianceProgramParticipantStatus.NotStarted && {
      label: "Change Status to: Calibrated",
      onClick: () =>
        handleUpdateCalibrationStatus(
          ComplianceProgramCalibrationStatus.Calibrated,
          "Calibrated",
        ),
    },
    submission.status === ComplianceProgramParticipantStatus.Complete && {
      label: "Change Status to: Not Calibrated",
      onClick: () =>
        handleUpdateCalibrationStatus(
          ComplianceProgramCalibrationStatus.NotStarted,
          "Not Calibrated",
        ),
    },
  ]);

  return (
    <>
      <ConfirmationDialog />
      <Dropdown disabled={loading} options={options}>
        <Button size={ButtonSize.medium} showDownChevronIcon>
          Actions
        </Button>
      </Dropdown>
    </>
  );
};

const SharingActions = ({
  submission,
  target,
}: {
  submission: RelatedAssessmentSubmissionFragment;
  target: BasicUser;
}) => {
  const label = useLabel();
  const currentUser = currentUserVar();

  const { ConfirmationDialog, confirm, setMessage } = useConfirm(
    "Are you sure?",
    "Are you sure?",
  );

  const [saveAssessmentDelivery, { loading }] = useMutation<
    SaveAssessmentDeliveryMutation,
    SaveAssessmentDeliveryMutationVariables
  >(updateAssessmentDeliveryMutation);

  const handleChangeDeliveryState = useCallback(
    async (state: AssessmentDeliveryState) => {
      if (!submission.delivery?.id) {
        return;
      }
      const confirmMsg = getDeliveryStateChangeConfirmation(state, {
        label,
        targetName: target.name,
        isTargetManager: currentUser.directReports.edges
          .map((node) => node?.node?.id)
          .includes(target.id),
      });
      const successMsg = getDeliveryStateChangeSuccess(state, label);
      setMessage(confirmMsg);
      const confirmation = await confirm();
      if (confirmation) {
        saveAssessmentDelivery({
          variables: {
            assessmentDeliveryId: submission.delivery.id,
            state,
          },
          refetchQueries: [getReviewSubmissionsQuery, getAssessmentDelivery],
          onCompleted: () => {
            successNotificationVar({
              title: successMsg,
            });
          },
        });
      }
    },
    [
      confirm,
      label,
      saveAssessmentDelivery,
      setMessage,
      submission,
      target,
      currentUser,
    ],
  );

  const options: DropdownOptionType[] = compact([
    submission.canCalibrate?.permission &&
      submission.template?.delivery === AssessmentGroupDelivery.AdminApproval &&
      submission.status === ComplianceProgramParticipantStatus.NotStarted && {
        label: "Change Status to: Approved for Sharing",
        onClick: () => handleChangeDeliveryState(AssessmentDeliveryState.Draft),
      },
    submission.canCalibrate?.permission &&
      submission.template?.delivery === AssessmentGroupDelivery.AdminApproval &&
      submission.status === ComplianceProgramParticipantStatus.InProgress && {
        label: "Change Status to: Not Approved",
        onClick: () =>
          handleChangeDeliveryState(
            AssessmentDeliveryState.AwaitingAdminApproval,
          ),
      },
    submission.status === ComplianceProgramParticipantStatus.Complete && {
      label: "Undo Sharing with Subject",
      onClick: () =>
        handleChangeDeliveryState(
          AssessmentDeliveryState.AwaitingAdminApproval,
        ),
    },
    submission.status !== ComplianceProgramParticipantStatus.Complete && {
      label: `Share ${label("review", { capitalize: true })} with Subject`,
      onClick: () =>
        handleChangeDeliveryState(AssessmentDeliveryState.Delivered),
    },
  ]);

  return (
    <>
      <ConfirmationDialog />
      <Dropdown disabled={loading} options={options}>
        <Button size={ButtonSize.medium} showDownChevronIcon>
          Actions
        </Button>
      </Dropdown>
    </>
  );
};

const RelatedSubmissionItemActions = ({
  submission,
  complianceProgramId,
  target,
}: {
  submission: RelatedAssessmentSubmissionFragment;
  complianceProgramId: number;
  target: BasicUser;
}) => {
  const label = useLabel();

  const [reopenAssessment, { loading: isReopeningAssessment }] = useMutation<
    UnsubmitAssessmentMutation,
    UnsubmitAssessmentMutationVariables
  >(reopenAssessmentMutation);

  const handleReopenAssessment = useCallback(
    (assessmentIds: number[]) => {
      reopenAssessment({
        variables: {
          assessmentIds,
        },
        refetchQueries: [getReviewSubmissionsQuery, getAssessmentDelivery],
        onCompleted: () => {
          successNotificationVar({
            title: `${label("review", {
              capitalize: true,
              pluralize: assessmentIds.length > 1,
            })} reopened.`,
          });
        },
      });
    },
    [label, reopenAssessment],
  );

  if (
    [
      AssessmentSubmissionNodeType.SelfAssessment,
      AssessmentSubmissionNodeType.ManagerAssessment,
    ].includes(submission.type)
  ) {
    return (
      <SingleSubmissionActions
        submission={submission}
        isDisabled={isReopeningAssessment}
        onReopenAssessment={(id) => handleReopenAssessment([id])}
      />
    );
  }
  if (
    [
      AssessmentSubmissionNodeType.PeerAssessment,
      AssessmentSubmissionNodeType.UpwardAssessment,
    ].includes(submission.type)
  ) {
    return (
      <MultiSubmissionActions
        submission={submission}
        isDisabled={isReopeningAssessment}
        onReopenAssessments={handleReopenAssessment}
      />
    );
  }
  if (
    AssessmentSubmissionNodeType.Calibration === submission.type &&
    submission.canCalibrate?.permission
  ) {
    return (
      <CalibrationActions
        submission={submission}
        complianceProgramId={complianceProgramId}
        target={target}
      />
    );
  }
  if (
    AssessmentSubmissionNodeType.Delivery === submission.type &&
    submission.delivery?.canUpdate?.permission
  ) {
    return <SharingActions submission={submission} target={target} />;
  }

  return <div></div>;
};

export default RelatedSubmissionItemActions;
